<app-base-modal
  [open]="open"
  (openChange)="onClose()"
  (beforeClose)="onBeforeClose($event)"
  size="2xl-full-h"
  title="Extraction Questions Schema"
>
  <!-- Content flows naturally inside the modal; the panel scrolls vertically
       (overflow-y-auto / overflow-x-hidden is applied by base-modal for size=2xl-full-h). -->
  <div class="space-y-4 relative">
    <!-- Alert / Banner Indicators -->
    @if (errorMessage) {
      <div class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {{ errorMessage }}
      </div>
    }

    @if (successMessage) {
      <div class="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
        {{ successMessage }}
      </div>
    }

    @if (isLocked) {
      <div class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
        <p class="font-bold">Questionnaire Schema Locked</p>
        <p class="mt-0.5 text-sm">
          The project has reached the data extraction phase. Modifying extraction questions is
          locked to preserve research data integrity.
        </p>
      </div>
    } @else if (!isEditable && !loading) {
      <!-- Read-only Role Alert -->
      <div class="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
        <p class="font-bold">Read-Only Mode</p>
        <p class="mt-0.5 text-sm">
          You are viewing this questionnaire schema because you are a project member. Only the
          Principal Investigator (PI) can edit the questions.
        </p>
      </div>
    }

    <!-- Core Domain Validation Errors List -->
    @if (validationErrors.length > 0) {
      <div class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        <h4 class="font-bold mb-2">⚠️ Questionnaire Validation Errors</h4>
        <ul class="list-disc pl-5 space-y-1">
          @for (err of validationErrors; track $index) {
            <li class="text-sm">{{ err }}</li>
          }
        </ul>
      </div>
    }

    <!-- Skeleton Loading -->
    @if (loading) {
      <div class="space-y-4">
        <div class="h-40 bg-slate-200 animate-pulse rounded-2xl"></div>
        <div class="h-40 bg-slate-200 animate-pulse rounded-2xl"></div>
        <div class="h-40 bg-slate-200 animate-pulse rounded-2xl"></div>
      </div>
    } @else {
      <!-- Question List Layout -->
      <div class="space-y-4">
        <!-- Case 1: PI INTERACTIVE EDITOR -->
        @if (isEditable) {
          <form [formGroup]="questionsForm" (submit)="$event.preventDefault()">
            <div formArrayName="questions" class="space-y-4">
              @if (questionsFormArray.length === 0) {
                <section class="card card-padding text-center py-12">
                  <div class="empty-state">
                    <div class="empty-state-icon">📋</div>
                    <h3 class="empty-state-title">No questions defined</h3>
                    <p class="empty-state-message">
                      Add your first custom field to get started with the schema.
                    </p>
                    <button type="button" (click)="addQuestion()" class="btn-primary mt-6">
                      + Add First Question
                    </button>
                  </div>
                </section>
              }

              @for (
                questionGroup of questionsFormArray.controls;
                track questionGroup;
                let idx = $index
              ) {
                <div
                  [formGroupName]="idx"
                  draggable="true"
                  (dragstart)="onDragStart($event, idx)"
                  (dragover)="onDragOver($event)"
                  (drop)="onDrop($event, idx)"
                  class="card card-padding relative group transition-all"
                >
                  <!-- Drag Handle Anchor -->
                  <div
                    class="absolute left-4 top-1/2 -translate-y-1/2 cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hidden lg:flex"
                    title="Drag to reorder"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="2.5"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M8 9h.01M16 9h.01M8 15h.01M16 15h.01M12 9h.01M12 15h.01"
                      />
                    </svg>
                  </div>

                  <!-- Editor Content -->
                  <div class="lg:pl-6">
                    <!-- Top control bar -->
                    <div
                      class="flex flex-wrap items-center justify-between gap-4 mb-4 pb-4 border-b border-slate-200"
                    >
                      <!-- Question Index -->
                      <div class="flex items-center gap-2">
                        <span
                          class="w-7 h-7 rounded-full bg-slate-100 text-slate-600 text-sm font-bold flex items-center justify-center"
                        >
                          {{ idx + 1 }}
                        </span>
                        <span class="text-sm font-bold text-slate-700">Question {{ idx + 1 }}</span>
                      </div>

                      <!-- Right Controls: Type Selector & Trash -->
                      <div class="flex items-center gap-2">
                        <!-- Question Type Dropdown -->
                        <select
                          formControlName="type"
                          (change)="changeQuestionType(idx, $any($event.target).value)"
                          class="select-field text-sm"
                        >
                          @for (opt of QUESTION_TYPES; track opt.value) {
                            <option [value]="opt.value">{{ opt.label }}</option>
                          }
                        </select>

                        <!-- Delete button -->
                        <button
                          type="button"
                          (click)="askRemoveQuestion(idx)"
                          class="btn-small-danger"
                          title="Delete Question"
                        >
                          Remove
                        </button>
                      </div>
                    </div>

                    <!-- Input Fields -->
                    <div class="space-y-4">
                      <!-- Question Text input -->
                      <div>
                        <label class="input-label">Question Prompt / Text</label>
                        <input
                          type="text"
                          formControlName="text"
                          placeholder="e.g., What is the reported confidence interval of the estimate?"
                          class="input-field"
                        />
                      </div>

                      <!-- Options List (CHOICE TYPES ONLY) -->
                      @if (
                        getQuestionFormGroup(idx).value.type === QuestionType.SINGLE_CHOICE ||
                        getQuestionFormGroup(idx).value.type === QuestionType.MULTI_CHOICE
                      ) {
                        <div class="p-4 bg-slate-50 rounded-lg border border-slate-200">
                          <div class="flex justify-between items-center mb-3">
                            <label class="input-label mb-0">Choice Options</label>
                            <button
                              type="button"
                              (click)="addOption(idx)"
                              class="text-sm font-semibold text-secondary hover:text-hover"
                            >
                              + Add Option
                            </button>
                          </div>

                          <div formArrayName="options" class="space-y-2">
                            @for (
                              optionControl of getOptionsFormArray(idx).controls;
                              track optionControl;
                              let optIdx = $index
                            ) {
                              <div class="flex items-center gap-2">
                                <input
                                  type="text"
                                  [formControlName]="optIdx"
                                  placeholder="Option value"
                                  class="input-field text-sm"
                                />
                                @if (getOptionsFormArray(idx).length > 1) {
                                  <button
                                    type="button"
                                    (click)="removeOption(idx, optIdx)"
                                    class="btn-small-danger"
                                    title="Remove option"
                                  >
                                    Remove
                                  </button>
                                }
                              </div>
                            }
                          </div>
                        </div>
                      }

                      <!-- Required Switch/Checkbox -->
                      <div class="flex items-center gap-2.5 pt-1">
                        <input
                          type="checkbox"
                          [id]="'req-' + idx"
                          formControlName="required"
                          class="w-4 h-4 cursor-pointer"
                        />
                        <label
                          [for]="'req-' + idx"
                          class="text-sm font-semibold text-slate-700 cursor-pointer select-none"
                        >
                          Mark as required field
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              }
            </div>

            <!-- Bottom Add Question Anchor -->
            <div class="mt-6">
              <button
                type="button"
                (click)="addQuestion()"
                class="w-full py-3 border-2 border-dashed border-slate-300 hover:border-slate-400 hover:bg-slate-50 text-slate-600 rounded-lg font-semibold flex items-center justify-center gap-2 transition-all"
              >
                <span class="text-lg">+</span> Add Question
              </button>
            </div>
          </form>
        }

        <!-- Case 2: READ-ONLY VIEWER (For researchers or locked states) -->
        @if (!isEditable) {
          @if (questionsFormArray.length === 0) {
            <section class="card card-padding text-center py-12">
              <div class="empty-state">
                <div class="empty-state-icon">📋</div>
                <h3 class="empty-state-title">No schema defined</h3>
                <p class="empty-state-message">
                  The Principal Investigator has not defined any extraction questions for this
                  project yet.
                </p>
              </div>
            </section>
          } @else {
            <!-- Render question cards in sequence -->
            @for (control of questionsFormArray.controls; track control; let idx = $index) {
              <app-extraction-question-card
                [question]="$any(control.value)"
                [index]="idx"
                [isEditable]="false"
              ></app-extraction-question-card>
            }
          }
        }
      </div>
    }

    <!-- Bottom action bar: Save Schema (PI + editable only) -->
    @if (isEditable && !loading) {
      <div class="sticky bottom-0 z-20 -mx-6 mt-4 border-t border-slate-200 bg-white px-6 py-4">
        <button
          type="button"
          (click)="saveQuestions()"
          [disabled]="saving"
          class="w-full btn-primary"
        >
          @if (saving) {
            <svg
              class="animate-spin h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Saving...
          } @else {
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"
              />
            </svg>
            &nbsp; Save Schema
          }
        </button>
      </div>
    }
  </div>
</app-base-modal>

<!-- Confirm Deletion of Extraction Question Modal -->
<app-confirm-modal
  [open]="showDeleteConfirm"
  title="Delete Question"
  message="Are you sure you want to delete this extraction question? This action cannot be undone."
  confirmText="Delete"
  cancelText="Cancel"
  confirmVariant="danger"
  size="sm"
  (openChange)="showDeleteConfirm = $event"
  (confirmed)="confirmRemoveQuestion()"
  (canceled)="cancelRemoveQuestion()"
></app-confirm-modal>

<!-- Confirm discard of unsaved changes when closing the schema modal -->
<app-confirm-modal
  [open]="showDiscardConfirm"
  title="Discard unsaved changes?"
  message="You have unsaved changes to the extraction questions schema. Closing now will discard them."
  confirmText="Discard changes"
  cancelText="Keep editing"
  confirmVariant="danger"
  size="sm"
  (openChange)="showDiscardConfirm = $event"
  (confirmed)="confirmDiscard()"
  (canceled)="cancelDiscard()"
></app-confirm-modal>
